/**
 * HTTP Utility
 * Uses Tauri's native HTTP client when available, falls back to fetch for web
 */

import { Platform } from '@/lib/platform'
import { logger } from '@/lib/logger'

export interface HttpOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  body?: string | Record<string, unknown>
  timeout?: number
  signal?: AbortSignal
}

export interface HttpResponse<T = any> {
  ok: boolean
  status: number
  statusText?: string
  data: T
  headers: Record<string, string>
}

/**
 * Make an HTTP request using the appropriate method for the platform
 */
export async function httpRequest<T = any>(
  url: string,
  options: HttpOptions = {}
): Promise<HttpResponse<T>> {
  const {
    method = 'GET',
    headers = {},
    body,
    timeout = 30000,
    signal
  } = options

  const isDesktop = Platform.isDesktop()
  logger.debug(`[HTTP] ${method} ${url} (isDesktop: ${isDesktop})`)

  // Use Tauri's native HTTP client if available
  if (isDesktop) {
    return tauriHttpRequest<T>(url, { method, headers, body, timeout, signal })
  }

  // Fall back to fetch for web
  return fetchHttpRequest<T>(url, { method, headers, body, timeout, signal })
}

/**
 * Tauri native HTTP request using custom Rust command
 * This bypasses certificate validation for self-signed certs
 */
async function tauriHttpRequest<T>(
  url: string,
  options: HttpOptions
): Promise<HttpResponse<T>> {
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    
    const { method, headers, body, timeout } = options
    
    // Prepare the request body
    let requestBody: string | undefined
    if (body) {
      requestBody = typeof body === 'string' ? body : JSON.stringify(body)
    }

    logger.debug(`[Tauri HTTP] ${method} ${url}`)

    const response = await invoke<{status: number, ok: boolean, headers: Record<string, string>, body: string}>('http_request', {
      request: {
        url,
        method: method || 'GET',
        headers: headers || {},
        body: requestBody,
        timeout_ms: timeout || 30000
      }
    })

    // Parse response data
    let data: T
    const contentType = response.headers['content-type'] || 
                       response.headers['Content-Type'] || ''
    
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(response.body) as T
      } catch {
        data = response.body as T
      }
    } else {
      data = response.body as T
    }

    return {
      ok: response.ok,
      status: response.status,
      statusText: response.ok ? 'OK' : 'Error',
      data,
      headers: response.headers
    }
  } catch (error) {
    logger.error(`[Tauri HTTP] Request failed:`, error)
    throw error
  }
}

/**
 * Browser fetch HTTP request
 */
async function fetchHttpRequest<T>(
  url: string,
  options: HttpOptions
): Promise<HttpResponse<T>> {
  const { method, headers, body, timeout, signal } = options

  // Create abort controller for timeout if no signal provided
  const controller = signal ? undefined : new AbortController()
  const timeoutId = controller && timeout 
    ? setTimeout(() => controller.abort(), timeout) 
    : undefined

  try {
    // Prepare the request body
    let requestBody: string | undefined
    if (body) {
      requestBody = typeof body === 'string' ? body : JSON.stringify(body)
    }

    logger.debug(`[Fetch] ${method} ${url}`)

    const response = await fetch(url, {
      method,
      headers: {
        ...headers,
        ...(typeof body === 'object' && !(headers && headers['Content-Type'])
          ? { 'Content-Type': 'application/json' } 
          : {})
      },
      body: requestBody,
      signal: signal || controller?.signal
    })

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Get response data
    let data: T
    const contentType = response.headers.get('content-type') || ''
    
    if (contentType.includes('application/json')) {
      data = await response.json() as T
    } else {
      data = await response.text() as T
    }

    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      data,
      headers: Object.fromEntries(response.headers.entries())
    }
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    logger.error(`[Fetch] Request failed:`, error)
    throw error
  }
}

/**
 * Convenience method for GET requests
 */
export async function httpGet<T = any>(
  url: string,
  options: Omit<HttpOptions, 'method' | 'body'> = {}
): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'GET' })
}

/**
 * Convenience method for POST requests
 */
export async function httpPost<T = any>(
  url: string,
  body?: string | Record<string, unknown>,
  options: Omit<HttpOptions, 'method' | 'body'> = {}
): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'POST', body })
}

/**
 * Convenience method for PUT requests
 */
export async function httpPut<T = any>(
  url: string,
  body?: string | Record<string, unknown>,
  options: Omit<HttpOptions, 'method' | 'body'> = {}
): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'PUT', body })
}

/**
 * Convenience method for DELETE requests
 */
export async function httpDelete<T = any>(
  url: string,
  options: Omit<HttpOptions, 'method' | 'body'> = {}
): Promise<HttpResponse<T>> {
  return httpRequest<T>(url, { ...options, method: 'DELETE' })
}
